import React, { useState } from 'react';
import { Grid, Box, useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, Typography, CircularProgress } from '@mui/material';
import BookingDesktopView from './bookingDesktopView';
import BookingMobileView from './bookingMobileView';
import BookingPagination from './bookingPagination';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import { ActionButton } from './styled';
import BookingSkeleton from './bookingSkeleton';

const BookingList = ({
  bookings,
  filters,
  handleSortChange,
  handlePageChange,
  navigateBooking,
  paginationInfo,
  resultLimit = 5,
  expandNextBooking,
  onBookingUpdated,
  loadingBookings=false
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const navigateSearch = () => navigate('/booking-find');
  const navigateVenue = (slug) => navigate(`/venue/${slug}`);

  // State for dialogs
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [checkOutDialogOpen, setCheckOutDialogOpen] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const statusMapping = {
    AL: 'All',
    AC: 'Confirmed',
    PC: 'Pending Confirmation',
    CA: 'Cancelled',
    FN: 'Finished',
    RJ: 'Rejected'
  };

  // Handler for check-in
  const handleCheckin = (booking) => {
    setCurrentBooking(booking);
    setCheckInDialogOpen(true);
  };

  // Handler for check-out
  const handleCheckout = (booking) => {
    setCurrentBooking(booking);
    setCheckOutDialogOpen(true);
  };

  // In BookingList component:

  // Confirm check-in
  const confirmCheckin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      setIsLoading(false);
      setCheckInDialogOpen(false);

      // Update the booking in the local state
      const updatedBookings = bookings.map(booking =>
        booking.slug === currentBooking.slug
          ? { ...booking, real_start: now }
          : booking
      );

      // If you're using state for bookings in the parent component,
      // you can call this callback to update it
      if (onBookingUpdated) {
        onBookingUpdated(updatedBookings);
      }

    } catch {
      setError('Failed to check in. Please try again.');
      setIsLoading(false);
    }
  };

  // Similarly for check-out
  const confirmCheckout = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      setIsLoading(false);
      setCheckOutDialogOpen(false);

      // Update the booking in the local state
      const updatedBookings = bookings.map(booking =>
        booking.slug === currentBooking.slug
          ? { ...booking, real_end: now }
          : booking
      );

      // If you're using state for bookings in the parent component
      if (onBookingUpdated) {
        onBookingUpdated(updatedBookings);
      }

    } catch {
      setError('Failed to check out. Please try again.');
      setIsLoading(false);
    }
  };

  if (loadingBookings) {
    return (
      <BookingSkeleton 
      rows={5} 
      expandFirstBooking={false} 
      showHeader={false}
    />
    )
  }
  
    

  if (isMobile) {
    return (
      <Box>
        <BookingMobileView
          bookings={bookings}
          navigateBooking={navigateBooking}
          navigateVenue={navigateVenue}
          statusMapping={statusMapping}
          navigateSearch={navigateSearch}
          t={t}
          resultLimit={resultLimit}
          handleCheckin={handleCheckin}
          handleCheckout={handleCheckout}
          expandNextBooking={expandNextBooking}
        />
        {paginationInfo && (
          <BookingPagination
            paginationInfo={paginationInfo}
            handlePageChange={handlePageChange}
            t={t}
          />
        )}

        {/* Check-in/Check-out Dialogs */}
        <CheckInDialog
          open={checkInDialogOpen}
          onClose={() => setCheckInDialogOpen(false)}
          onConfirm={confirmCheckin}
          booking={currentBooking}
          isLoading={isLoading}
          error={error}
        />

        <CheckOutDialog
          open={checkOutDialogOpen}
          onClose={() => setCheckOutDialogOpen(false)}
          onConfirm={confirmCheckout}
          booking={currentBooking}
          isLoading={isLoading}
          error={error}
        />
      </Box>
    );
  }

  return (
    <Grid container spacing={3} sx={{ px: 2.5 }}>
      <Grid item xs={12}>
        <BookingDesktopView
          bookings={bookings}
          filters={filters}
          handleSortChange={handleSortChange}
          navigateBooking={navigateBooking}
          navigateVenue={navigateVenue}
          statusMapping={statusMapping}
          navigateSearch={navigateSearch}
          t={t}
          showHeader={false}
          resultLimit={resultLimit}
          expandNextBooking={expandNextBooking}
          handleCheckin={handleCheckin}
          handleCheckout={handleCheckout}
        />
        {paginationInfo && (
          <BookingPagination
            paginationInfo={paginationInfo}
            handlePageChange={handlePageChange}
            t={t}
          />
        )}

        {/* Check-in/Check-out Dialogs */}
        <CheckInDialog
          open={checkInDialogOpen}
          onClose={() => setCheckInDialogOpen(false)}
          onConfirm={confirmCheckin}
          booking={currentBooking}
          isLoading={isLoading}
          error={error}
        />

        <CheckOutDialog
          open={checkOutDialogOpen}
          onClose={() => setCheckOutDialogOpen(false)}
          onConfirm={confirmCheckout}
          booking={currentBooking}
          isLoading={isLoading}
          error={error}
        />
      </Grid>
    </Grid>
  );
};

// Check-in Dialog Component with improved styling
const CheckInDialog = ({ open, onClose, onConfirm, booking, isLoading, error }) => {
  const theme = useTheme();
  const currentTime = dayjs().format('HH:mm');
  const currentDate = dayjs().format('DD MMM YYYY');

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xs" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: theme.shadows[10]
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 0,
        pt: 2.5,
        fontWeight: 600,
        fontSize: '1.2rem'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CheckIcon sx={{ color: 'success.main', mr: 1.5 }} />
          Check-in to Workspace
        </Box>
      </DialogTitle>
      <DialogContent sx={{ py: 3 }}>
        {booking && (
          <>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to check in to <strong>{booking.venue?.name}</strong>?
            </Typography>

            <Box sx={{ 
              mt: 3, 
              p: 2.5, 
              bgcolor: 'background.paper', 
              borderRadius: '12px', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                Current time:
              </Typography>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}>
                {currentTime} - {currentDate}
              </Typography>

              {booking.real_start && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                    Previous check-in:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {dayjs(booking.real_start).format('HH:mm - DD MMM YYYY')}
                  </Typography>
                </Box>
              )}
            </Box>

            {error && (
              <Box sx={{ 
                mt: 2, 
                p: 1.5, 
                bgcolor: 'error.lighter', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <InfoOutlinedIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                <Typography color="error.main" variant="body2">
                  {error}
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ 
        px: 3, 
        pb: 3,
        display: 'flex',
        gap: 2 
      }}>
        <ActionButton
          onClick={onClose}
          disabled={isLoading}
          variant="outlined"
          fullWidth
        >
          Cancel
        </ActionButton>
        <ActionButton
          onClick={onConfirm}
          variant="contained"
          color="success"
          disabled={isLoading}
          fullWidth
          sx={{
            position: 'relative',
            '& .MuiCircularProgress-root': {
              position: 'absolute',
              left: '50%',
              top: '50%',
              marginLeft: '-10px',
              marginTop: '-10px'
            }
          }}
        >
          {isLoading ? (
            <>
              <span style={{ opacity: 0 }}>Check In</span>
              <CircularProgress size={20} color="inherit" />
            </>
          ) : (
            'Check In'
          )}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

// Check-out Dialog Component with improved styling
const CheckOutDialog = ({ open, onClose, onConfirm, booking, isLoading, error }) => {
  const theme = useTheme();
  const currentTime = dayjs().format('HH:mm');
  const currentDate = dayjs().format('DD MMM YYYY');

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xs" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: theme.shadows[10]
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 0,
        pt: 2.5,
        fontWeight: 600,
        fontSize: '1.2rem'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LogoutIcon sx={{ color: 'error.main', mr: 1.5 }} />
          Check-out from Workspace
        </Box>
      </DialogTitle>
      <DialogContent sx={{ py: 3 }}>
        {booking && (
          <>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to check out from <strong>{booking.venue?.name}</strong>?
            </Typography>

            <Box sx={{ 
              mt: 3, 
              p: 2.5, 
              bgcolor: 'background.paper', 
              borderRadius: '12px', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                Current time:
              </Typography>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}>
                {currentTime} - {currentDate}
              </Typography>

              {booking.real_start && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                    Checked in at:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {dayjs(booking.real_start).format('HH:mm - DD MMM YYYY')}
                  </Typography>
                </Box>
              )}

              {booking.real_end && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                    Previous check-out:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {dayjs(booking.real_end).format('HH:mm - DD MMM YYYY')}
                  </Typography>
                </Box>
              )}
            </Box>

            {error && (
              <Box sx={{ 
                mt: 2, 
                p: 1.5, 
                bgcolor: 'error.lighter', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <InfoOutlinedIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                <Typography color="error.main" variant="body2">
                  {error}
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ 
        px: 3, 
        pb: 3,
        display: 'flex',
        gap: 2 
      }}>
        <ActionButton
          onClick={onClose}
          disabled={isLoading}
          variant="outlined"
          fullWidth
        >
          Cancel
        </ActionButton>
        <ActionButton
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={isLoading}
          fullWidth
          sx={{
            position: 'relative',
            '& .MuiCircularProgress-root': {
              position: 'absolute',
              left: '50%',
              top: '50%',
              marginLeft: '-10px',
              marginTop: '-10px'
            }
          }}
        >
          {isLoading ? (
            <>
              <span style={{ opacity: 0 }}>Check Out</span>
              <CircularProgress size={20} color="inherit" />
            </>
          ) : (
            'Check Out'
          )}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default BookingList;